/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useCallback, useEffect, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { type MessageDescriptor, useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router-next';

import Button, { LinkButton } from '@atlaskit/button/new';
import { css, cssMap, cx, jsx } from '@atlaskit/css';
import type { EmojiProvider } from '@atlaskit/emoji/resource';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { addRovoParamsToUrl } from '@atlaskit/rovo-triggers/params';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import {
	buildDynamicUiPageUrl,
	isDynamicUiCreateAction,
	isRemixGeneratedFlow,
	isImageCreateAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { copyImageToClipboard } from '@atlassian/conversation-assistant-action-helpers/utils/hooks/copyImageToClipboard';
import { isJiraCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { isJsmCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jsm-actions';
import { getAtlasURL } from '@atlassian/conversation-assistant-agent/utils/getAtlasURL';
import {
	ExperienceName,
	PreviewCardRenderStage,
	PreviewContentStage,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	ExperienceFailure,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { MessageActionResolutions } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import {
	ContentType,
	getContentTypeFromPublishedContent,
} from '@atlassian/conversation-assistant-store/controllers/preview-content/contentType';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useAifcAnalytics } from '@atlassian/jira-create-work-items/useAifcAnalytics.tsx';
import { getPreviewItemIconConfig } from '@atlassian/rovo-platform-ui-components/preview-item/content-type-icon-config';
import { PreviewItemIcon } from '@atlassian/rovo-platform-ui-components/preview-item/PreviewItemIcon';
import { findMessageById, getAIFCActionStatus } from '@atlassian/smart-creation/utils';

import DashboardHeaderImage from '../../assets/preview-card-dashboard-header.svg';
import DatabaseHeaderImage from '../../assets/preview-card-database-header.svg';
import PageHeaderImage from '../../assets/preview-card-page-header.svg';
import SlidesHeaderImage from '../../assets/preview-card-slides-header.svg';
import WhiteboardHeaderImage from '../../assets/preview-card-whiteboard-header.svg';
import { ConfluenceLogo } from '../../assets/product-logos';
import { BasePreviewCard } from '../../common/ui/base-preview-card';

import { DetailsRow } from './details-row';
import { DismissableTooltip } from './dismissable-tooltip';
import { useImageUrl } from './hooks/useImageUrl';
import { JiraCreateErrorBoundaryFallback } from './jira-create-error-boundary-fallback';
import { JiraCreateWithRovoPreviewCard } from './jira-create-with-rovo-preview-card';
import { JsmCreateSurveyCard } from './jsm-create-survey-card/JsmCreateSurveyCard.tsx';
import { JsmCreateSurveyCardError } from './jsm-create-survey-card/JsmCreateSurveyCardError.tsx';
import { messages } from './messages';
import { useProductData } from './useProductData';

const appendPreviewParam = (url: string): string => {
	try {
		const parsed = new URL(url);
		parsed.searchParams.set('preview', 'true');
		return parsed.toString();
	} catch {
		return url.includes('?') ? `${url}&preview=true` : `${url}?preview=true`;
	}
};

const styles = cssMap({
	containerBase: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		width: '320px',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
		'&:hover': {
			boxShadow: token('elevation.shadow.overlay'),
		},
	},
	containerTransparentBorder: {
		borderColor: token('color.border'),
	},
	containerWithBorder: {
		borderColor: token('color.border.focused'),
	},
	imageContainer: {
		position: 'relative',
	},
	icon: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
	},
	textContainer: {
		minHeight: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	dynamicUiContainer: {
		width: '100%',
		height: '400px',
	},
	dynamicUiStack: {
		height: '100%',
	},
	iframe: {
		width: '100%',
		height: '100%',
		border: 'none',
	},
	imageCreateContainer: {
		width: '100%',
		maxHeight: '240px',
		overflow: 'hidden',
	},
	avpCardContainer: {
		width: '320px',
	},
});

const imageCreateImageStyle = css({
	width: '100%',
	display: 'block',
	objectFit: 'cover',
});

const getProductLogo = (product: string) => {
	if (product === 'confluence') {
		return <ConfluenceLogo />;
	}
	return null;
};

const getContentToHeaderImage = (contentType: ContentType) => {
	switch (contentType) {
		case ContentType.liveDoc:
			return PageHeaderImage;
		case ContentType.whiteboard:
			return WhiteboardHeaderImage;
		case ContentType.database:
			return DatabaseHeaderImage;
		case ContentType.slides:
			return SlidesHeaderImage;
		case ContentType.dashboard:
			return DashboardHeaderImage;
	}
};

const CustomButtonWithPreviewDefault = ({
	defaultButton,
	links,
	formatMessage,
	conversationId,
	shouldOpenPreviewInAtlassianHome,
	cloudId,
	messageId,
	isPreviewDisabled,
}: {
	defaultButton: React.ReactNode;
	links?: {
		webui: string;
		base: string;
	};
	formatMessage: (messageDescriptor: MessageDescriptor) => string;
	conversationId?: string;
	shouldOpenPreviewInAtlassianHome?: boolean;
	cloudId?: string;
	messageId?: string;
	isPreviewDisabled?: boolean;
}) => {
	if (links?.webui) {
		const { base, webui } = links;
		const host = window.location.host;
		const linkToOpen = `${base}${webui}`;

		return (
			<LinkButton
				target={base.includes(host) ? '_self' : '_blank'}
				href={linkToOpen}
				spacing="compact"
			>
				{formatMessage(messages.openButton)}
			</LinkButton>
		);
	}

	// if shouldOpenPreviewInAtlassianHome is true, we want to open all previews in
	// Atlassian home with the current conversation and staging area open - this is
	// to better support creation flows where the content staging area isn't supported
	if (shouldOpenPreviewInAtlassianHome) {
		const rovoPath = addRovoParamsToUrl('', {
			pathway: 'chat',
			conversationId: conversationId ?? undefined,
			stagingAreaOpen: true,
			messageIdSelectedForPreview: messageId,
		});

		const atlassianHomeUrl = getAtlasURL(cloudId || '', `/chat${rovoPath}`, undefined);

		return (
			<LinkButton
				target="_blank"
				href={atlassianHomeUrl}
				spacing="compact"
				testId="home-preview-button"
				isDisabled={isPreviewDisabled}
			>
				{formatMessage(messages.previewButton)}
			</LinkButton>
		);
	}

	return defaultButton;
};

export const SmartCreatePreviewCard = ({
	previewProduct, // Product of the content which is displayed in the preview card
	product, // Product of where the preview card is being displayed
	contentType,
	title,
	isSelected,
	isEnabled,
	isLastMessage,
	contentId,
	emojiProvider,
	stagingAreaEnabled,
	fullScreen,
	conversationId,
	messageId,
	status,
	cloudId,
	shouldOpenPreviewInAtlassianHome,
	resolveActions,
	resolveAction,
	invocationId,
	showSuccessFlagOnCreate,
}: {
	previewProduct: string;
	product?: string;
	contentType: ContentType | null;
	title: string;
	isSelected: boolean;
	isEnabled?: boolean;
	isLastMessage?: boolean;
	contentId: string;
	emojiProvider?: Promise<EmojiProvider>;
	stagingAreaEnabled?: boolean;
	fullScreen?: boolean;
	conversationId?: string;
	messageId: string;
	status?: string;
	cloudId: string;
	shouldOpenPreviewInAtlassianHome?: boolean;
	resolveActions?: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationIds: string[],
	) => void;
	resolveAction?: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationId: string,
	) => void;
	invocationId: string;
	/**
	 * Controls whether a success flag is shown after Jira work items are created.
	 * Defaults to `true`. Surfaces such as rovo-chat-desktop can pass `false` to
	 * suppress the AFM success flag and surface their own confirmation.
	 */
	showSuccessFlagOnCreate?: boolean;
}): React.JSX.Element | null => {
	const [showDynamicUiCopied, setShowDynamicUiCopied] = useState(false);
	const [showImageCopied, setShowImageCopied] = useState(false);
	const [dynamicUiCopiedDismissKey, setDynamicUiCopiedDismissKey] = useState(0);
	const [imageCopiedDismissKey, setImageCopiedDismissKey] = useState(0);
	const { formatMessage } = useIntl();
	const copyText = formatMessage(messages.dynamicUiCopyButton);
	const copiedText = formatMessage(messages.dynamicUiCopied);
	const onDynamicUiCopiedDismiss = useCallback(() => setShowDynamicUiCopied(false), []);
	const onImageCopiedDismiss = useCallback(() => setShowImageCopied(false), []);

	const experienceTracker = useAIMateExperienceTracker();
	const { sendAifcAnalyticsEvent } = useAifcAnalytics(messageId);
	const publish = usePublish('ai-mate');
	const { publishedContentData } = useProductData({
		previewProduct,
		id: contentId,
		cloudId,
		includeReactions: contentType === ContentType.liveDoc,
		status,
	});

	// Update attributes for the aiMatePreviewCardRender experience
	useEffect(() => {
		experienceTracker.updateAttributes({
			name: ExperienceName.PREVIEW_CARD_RENDER,
			attributes: {
				[PreviewCardRenderStage.CARD_MOUNTED_AT]: performance.now(),
			},
		});
	}, [experienceTracker]);
	const isNonInstrumentedPreviewBranch =
		previewProduct === 'jira' || previewProduct === 'jsm' || previewProduct === 'avp';
	useEffect(() => {
		if (isNonInstrumentedPreviewBranch) {
			experienceTracker.updateAttributes({
				name: ExperienceName.PREVIEW_CARD_RENDER,
				attributes: {
					[PreviewCardRenderStage.ROUTED_TO_NON_INSTRUMENTED_BRANCH_AT]: performance.now(),
				},
			});
		}
	}, [isNonInstrumentedPreviewBranch, experienceTracker]);

	const {
		setStagingAreaMode,
		setMessageIdSelectedForPreview,
		setInvocationIdSelectedForPreview,
		getInvocationIdSelectedForPreview,
		getMessageIdSelectedForPreview,
	} = useStagingAreaActions();
	const { push } = useRouterActions();

	// Stable reference to track whether the Canvas is open or not using global sweet state values
	// stagingAreaEnabled is not a stable reference since the React Context is created for each instance of ConversationAssistant
	const isStagingArea = !!getMessageIdSelectedForPreview() || !!getInvocationIdSelectedForPreview();

	const [chatMessages] = useChatMessages({ conversationId: conversationId ?? '' });
	const selectedMessageForStatus = findMessageById(chatMessages, messageId);

	const isMultiCreateEnabled = FeatureGates.getExperimentValue<boolean>(
		'cwr-be-based-multi-creation-v0',
		'isEnabled',
		false,
	);

	const previewStatus = getAIFCActionStatus(
		selectedMessageForStatus,
		isMultiCreateEnabled ? invocationId : undefined,
	);

	// For published content, resolve the content type from type/subType. For drafts, use contentType directly.
	const resolvedContentType = publishedContentData
		? getContentTypeFromPublishedContent(publishedContentData.type, publishedContentData.subType)
		: contentType;
	const iconConfig = getPreviewItemIconConfig(resolvedContentType);
	const headerImage = contentType ? getContentToHeaderImage(contentType) : null;

	// actions used to determine jira and dynamic ui preview flows
	const actions = selectedMessageForStatus?.content.message.message.actions ?? [];
	// A message can hold multiple creatable actions; resolve the one this card represents by
	// invocationId, falling back to the first action.
	const selectedAction =
		(isMultiCreateEnabled &&
			invocationId &&
			actions.find((action) => action.invocationId === invocationId)) ||
		actions[0];

	// actions that have been invoked by the user
	const invokedActions = selectedMessageForStatus?.invokedActions;

	// The remix/MAUI action this card represents: its own selected action under multi-create, otherwise
	// the first remix action in the message (control path — preserves master for legacy multi-action
	// messages whose remix action isn't first).
	const remixCandidate = isMultiCreateEnabled ? selectedAction : actions.find(isRemixGeneratedFlow);
	const mauiAction = remixCandidate && isRemixGeneratedFlow(remixCandidate) ? remixCandidate : null;
	const isDynamicUiAction = mauiAction !== null && isDynamicUiCreateAction(mauiAction);
	const dynamicUiUrl =
		isDynamicUiAction && expValEquals('cc-maui-experiment', 'isEnabled', true)
			? buildDynamicUiPageUrl(mauiAction.data)
			: null;
	const isDynamicUi = !!dynamicUiUrl;
	// `preview=true` is only added for the iframe so the Copy button still yields a shareable URL.
	const dynamicUiIframeUrl = dynamicUiUrl ? appendPreviewParam(dynamicUiUrl) : undefined;
	const isMauiPreview =
		mauiAction !== null && expValEquals('cc-maui-experiment', 'isEnabled', true);

	const isOpenInCurrentTab = () => {
		if (publishedContentData?.links?.webui && publishedContentData?.links?.base) {
			const { base, webui } = publishedContentData.links;
			const linkToOpen = `${base}${webui}`;
			const currentUrl = window.location.href;
			return currentUrl.includes(webui) || currentUrl === linkToOpen;
		}
		return false;
	};

	const isInAtlassianHome =
		window.location.href.includes('home.atlassian.com') ||
		window.location.href.includes('home.stg.atlassian.com');
	const isDefaultPreviewDisabled =
		isSelected || (isInAtlassianHome && !fullScreen && previewStatus !== 'complete');
	const isHomePreviewDisabled =
		isDefaultPreviewDisabled || (shouldOpenPreviewInAtlassianHome && previewStatus !== 'complete');

	const isJiraCreateAction = isJiraCreatableAction(selectedAction);
	const isJsmCreateAction = isJsmCreatableAction(selectedAction);

	// The image-create action this card represents; resolved the same way as `mauiAction`.
	const imageCandidate = isMultiCreateEnabled ? selectedAction : actions.find(isImageCreateAction);
	const imageCreateAction =
		imageCandidate && isImageCreateAction(imageCandidate) ? imageCandidate : undefined;
	// The infographic experiment is per-host-product (cc-maui-experiment on Confluence,
	// jira_maui_remix on Jira). expValEquals remaps via tmp-editor-statsig's global _product,
	// but on rovo chat that global is always 'confluence' (only the Jira editor calls
	// setupEditorExperiments('jira')), so on Jira it read the wrong (Confluence) experiment and
	// the thumbnail stayed blank. Key off the card's own `product` prop instead.
	const isImageCreate =
		!!imageCreateAction &&
		FeatureGates.getExperimentValue<boolean>(
			product === 'jira' ? 'jira_maui_remix' : 'cc-maui-experiment',
			'isEnabled',
			false,
		);
	const imageUrl = useImageUrl(imageCreateAction?.data.mediaFileId, cloudId);

	const onPreviewClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			experienceTracker.start({
				name: ExperienceName.PREVIEW_CONTENT,
				id: messageId,
				timeout: 30000,
				attributes: {
					expectedContentType: contentType,
				},
			});
			experienceTracker.updateAttributes({
				name: ExperienceName.PREVIEW_CONTENT,
				attributes: {
					[PreviewContentStage.STARTED_AT]: performance.now(),
				},
			});

			if (
				invocationId &&
				!(isJiraCreateAction && fg('jira_aifc_enabled')) &&
				!(isJsmCreateAction && fg('surveys-aifc'))
			) {
				setInvocationIdSelectedForPreview(invocationId);
			} else {
				setMessageIdSelectedForPreview(messageId);
			}

			if (fullScreen && isInAtlassianHome) {
				// If we are in Atlassian home and already fullscreen, render the staging area next to chat
				setStagingAreaMode('inline', 'rovoChatPreviewCard');
			} else if (isInAtlassianHome) {
				// If we are in Atlassian home and not already in fullscreen chat, redirect to fullscreen chat and set the staging area to be open
				const path = addRovoParamsToUrl('', {
					pathway: 'chat',
					conversationId: conversationId ?? undefined,
					stagingAreaOpen: true,
					messageIdSelectedForPreview: messageId,
				});
				push(`/chat${path}`);
			} else if (!stagingAreaEnabled) {
				if (
					isMauiPreview &&
					contentType !== ContentType.slides &&
					contentType !== ContentType.database
				) {
					if (product === 'confluence' && fg('remix_product_modal_rovo_chat')) {
						// When in Confluence, open the product-specific AIFC modal so the
						// Ephemeral Preview can stay mounted when the Rovo side chat closes.
						publish({
							type: 'open-smart-creation-modal',
							source: 'rovo-chat-preview-card',
							data: {
								entryPoint: 'rovoChatPreviewCard',
								stagingAreaActive: true,
								isFloating: true,
								shouldShowBackButton: false,
							},
						});
					} else {
						setStagingAreaMode('ephemeralModal', 'rovoChatPreviewCard');
					}
				} else {
					setStagingAreaMode('modal', 'rovoChatPreviewCard');
				}
			}
		},
		[
			setMessageIdSelectedForPreview,
			stagingAreaEnabled,
			setStagingAreaMode,
			conversationId,
			fullScreen,
			isInAtlassianHome,
			messageId,
			push,
			experienceTracker,
			contentType,
			invocationId,
			setInvocationIdSelectedForPreview,
			isMauiPreview,
			isJiraCreateAction,
			isJsmCreateAction,
			product,
			publish,
		],
	);

	const onJiraAifcPreviewClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			sendAifcAnalyticsEvent?.({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createWorkItemsAifcPreviewOpened',
				attributes: {
					itemCount: actions.length,
					// Only available in multiple work item preview card
					isSingleWorkItem: false,
					isStagingArea,
				},
			});
			onPreviewClick(e);
		},
		[onPreviewClick, sendAifcAnalyticsEvent, isStagingArea, actions.length],
	);

	const onDynamicUiCopyClick = useCallback(async () => {
		if (dynamicUiUrl) {
			await navigator.clipboard.writeText(dynamicUiUrl);
			setDynamicUiCopiedDismissKey((key) => key + 1);
			setShowDynamicUiCopied(true);
		}
	}, [dynamicUiUrl]);

	// Show the new preview card for Jira when the feature flag is enabled
	if (previewProduct === 'jira') {
		if (resolveActions && actions.length > 0 && isJiraCreateAction) {
			if (fg('jira-aifc-migrate-action-renderer')) {
				return null;
			}

			return (
				<ErrorBoundary
					fallbackRender={({ error }) => (
						<>
							<ExperienceFailure error={error} name={ExperienceName.JIRA_PREVIEW_CARD_RENDER} />
							{/* Action Plan reliability signal: the create proposal failed to render
							    (RovoSkillsPlanWorkItemCreate). The matching start (which carries the
							    isStagingArea surface attribute) is fired by JiraCreateWithRovoPreviewCard,
							    so this fail is not a no-op and inherits that attribute. */}
							{fg('rovo_skills_work_item_reliability_ufo') && (
								<ExperienceFailure
									error={error}
									name={ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_CREATE}
								/>
							)}
							<JiraCreateErrorBoundaryFallback
								error={error}
								sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
							/>
						</>
					)}
				>
					<JiraCreateWithRovoPreviewCard
						product={product}
						cloudId={cloudId}
						actions={actions}
						messageId={messageId}
						conversationId={conversationId ?? ''}
						invokedActions={invokedActions}
						isSelected={isSelected}
						isEnabled={isEnabled}
						isLastMessage={isLastMessage}
						onPreviewClick={onJiraAifcPreviewClick}
						resolveActions={resolveActions}
						isStagingArea={isStagingArea}
						showSuccessFlagOnCreate={showSuccessFlagOnCreate}
					/>
					<ExperienceSuccess name={ExperienceName.JIRA_PREVIEW_CARD_RENDER} />
				</ErrorBoundary>
			);
		} else {
			// When the feature gate is disabled, we default to the UI shown for the bulk create action
			return null;
		}
	}

	// Show the new preview card for JSM when the feature flag is enabled
	if (previewProduct === 'jsm') {
		if (isJsmCreateAction && fg('surveys-aifc')) {
			return (
				<ErrorBoundary
					fallbackRender={({ error }) => (
						<>
							<ExperienceFailure error={error} name={ExperienceName.JSM_PREVIEW_CARD_RENDER} />
							<JsmCreateSurveyCardError />
						</>
					)}
				>
					<JsmCreateSurveyCard
						actions={actions}
						resolveAction={resolveAction}
						isSelected={isSelected}
						isEnabled={isEnabled}
						stagingAreaEnabled={stagingAreaEnabled}
						cloudId={cloudId}
						onPreview={onPreviewClick}
						conversationId={conversationId}
					/>
					<ExperienceSuccess name={ExperienceName.JSM_PREVIEW_CARD_RENDER} />
				</ErrorBoundary>
			);
		} else {
			return null;
		}
	}

	if (previewProduct === 'avp') {
		if (!fg('platform_avp_aifc_enabled')) {
			return null;
		}
		return (
			<Box xcss={styles.avpCardContainer}>
				<BasePreviewCard
					testId="conversation-assistant-preview-card.avp-dashboard-preview-card"
					previewProductLogo={
						<DashboardIcon color={token('color.icon.accent.blue')} label="Dashboard" />
					}
					isSelected={isSelected}
					isEnabled={isEnabled}
					previewHeaderTitle={
						<Text size="medium" weight="bold">
							{formatMessage(messages.createDashboard)}
						</Text>
					}
					previewContent={
						<Stack space="space.100">
							<Box xcss={styles.imageContainer}>
								<Image src={DashboardHeaderImage} alt="" />
							</Box>
							<Text weight="medium" color="color.link">
								{title}
							</Text>
						</Stack>
					}
					previewFooterActions={
						isSelected || isOpenInCurrentTab() ? (
							<Box xcss={styles.textContainer}>
								<Text color="color.text.subtlest">{formatMessage(messages.viewingNow)}</Text>
							</Box>
						) : (
							<Button
								onClick={onPreviewClick}
								interactionName="rovo-aifc-avp-dashboard-preview-card-click"
								spacing="compact"
							>
								{formatMessage(messages.newPreviewButton)}
							</Button>
						)
					}
				/>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.containerBase,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				isSelected ? styles.containerWithBorder : styles.containerTransparentBorder,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				isDynamicUi && styles.dynamicUiContainer,
			)}
			tabIndex={
				FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
					fireExperimentExposure: false,
				})
					? undefined
					: 0
			}
		>
			<Stack space="space.100" xcss={isDynamicUi ? styles.dynamicUiStack : undefined}>
				{isDynamicUi ? (
					<iframe
						css={[styles.iframe]}
						src={dynamicUiIframeUrl}
						title={title || formatMessage(messages.dynamicUiPreviewTitle)}
					/>
				) : isImageCreate && imageUrl ? (
					<Box xcss={styles.imageCreateContainer}>
						<Image
							src={imageUrl}
							alt={imageCreateAction?.data?.title || ''}
							css={imageCreateImageStyle}
						/>
					</Box>
				) : (
					<>
						<Box xcss={styles.imageContainer}>
							<Image src={headerImage ?? undefined} alt="" />
							{iconConfig && (
								<Box xcss={styles.icon}>
									<PreviewItemIcon contentType={resolvedContentType} bold size="medium" />
								</Box>
							)}
						</Box>
						<Text weight="medium" color="color.link">
							{publishedContentData?.title ?? title}
						</Text>
						<DetailsRow
							publishedContentData={publishedContentData}
							emojiProvider={emojiProvider}
							product={previewProduct}
							cloudId={cloudId}
						/>
					</>
				)}
				<Inline spread="space-between" alignBlock="center">
					{getProductLogo(previewProduct)}
					<Inline space="space.100">
						{isDynamicUi && (
							<DismissableTooltip
								content={copiedText}
								isVisible={showDynamicUiCopied}
								dismissKey={dynamicUiCopiedDismissKey}
								onDismiss={onDynamicUiCopiedDismiss}
								testId="conversation-assistant-preview-card.dynamic-ui-copied-tooltip"
							>
								<Button onClick={onDynamicUiCopyClick} spacing="compact" appearance="subtle">
									{copyText}
								</Button>
							</DismissableTooltip>
						)}
						{isImageCreate && imageUrl && (
							<DismissableTooltip
								content={copiedText}
								isVisible={showImageCopied}
								dismissKey={imageCopiedDismissKey}
								onDismiss={onImageCopiedDismiss}
								testId="conversation-assistant-preview-card.image-copied-tooltip"
							>
								<Button
									onClick={async () => {
										if (imageUrl) {
											await copyImageToClipboard(imageUrl);
											setImageCopiedDismissKey((key) => key + 1);
											setShowImageCopied(true);
										}
									}}
									spacing="compact"
									appearance="subtle"
								>
									{copyText}
								</Button>
							</DismissableTooltip>
						)}
						{isSelected || isOpenInCurrentTab() ? (
							<Box xcss={styles.textContainer}>
								<Text color="color.text.subtlest">{formatMessage(messages.viewingNow)}</Text>
							</Box>
						) : isImageCreate ? (
							<Button
								isDisabled={isDefaultPreviewDisabled}
								onClick={onPreviewClick}
								interactionName="rovo-aifc-preview-card-click"
								spacing="compact"
							>
								{formatMessage(messages.dynamicUiExpandButton)}
							</Button>
						) : (
							<CustomButtonWithPreviewDefault
								defaultButton={
									<Button
										//Temporary solution - If we are in Atlassian home and the streaming isn't complete, don't allow user to use preview button
										isDisabled={isDefaultPreviewDisabled}
										onClick={onPreviewClick}
										interactionName="rovo-aifc-preview-card-click"
										spacing="compact"
									>
										{isMauiPreview
											? formatMessage(messages.dynamicUiExpandButton)
											: formatMessage(messages.previewButton)}
									</Button>
								}
								links={publishedContentData?.links}
								formatMessage={formatMessage}
								conversationId={conversationId}
								shouldOpenPreviewInAtlassianHome={shouldOpenPreviewInAtlassianHome}
								cloudId={cloudId}
								messageId={messageId}
								isPreviewDisabled={isHomePreviewDisabled}
							/>
						)}
					</Inline>
				</Inline>
			</Stack>
			<ExperienceSuccess name={ExperienceName.PREVIEW_CARD_RENDER} />
		</Box>
	);
};
